import { useMutation } from '@tanstack/react-query';
import { create } from 'zustand';
import { authService } from '@/api/services/auth';
import { clearAllAiChatThreads } from '@/lib/aiChatIndexedDb';
import { authHelper } from '@/lib/localStore';
import type { QueryKey, UseMutationOptions } from '@tanstack/react-query';
import type { LoginRequest, LoginResponse, User } from '@/types';

export const useUser = create<{
  user: User | null;
  setUser: (user: User | null) => void;
  token: string | null;
  refreshToken: string | null;
  setToken: (token: string) => void;
  setRefreshToken: (refreshToken: string) => void;
  logoutUser: () => void;
}>((set) => ({
  user: authHelper.user,
  setUser: (user) => {
    set({ user });
    authHelper.user = user;
  },
  token: authHelper.token,
  refreshToken: authHelper.refreshToken,
  setToken: (token) => set({ token }),
  setRefreshToken: (refreshToken) => set({ refreshToken }),
  logoutUser: () => {
    set({ user: null, token: null, refreshToken: null });
    authHelper.user = null;
    authHelper.token = null;
    authHelper.refreshToken = null;
    localStorage.clear();
    void clearAllAiChatThreads();
  },
}));

export const useLoginQueryKey = () => ['login'] as QueryKey;

/**
 * Login mutation hook
 */
export const useLogin = (
  options?: UseMutationOptions<
    { status: boolean; data?: LoginResponse; error?: { code: string; message: string } },
    Error,
    LoginRequest
  >
) =>
  useMutation({
    mutationFn: authService.login,
    mutationKey: useLoginQueryKey(),
    ...options,
    onSuccess: (data) => {
      if (data.status) {
        authHelper.token = data.data?.token || '';
        authHelper.refreshToken = data.data?.refreshToken || '';
        authHelper.user = data.data?.data || null;
      }
      return data;
    },
  });


/**
 * Logout mutation key
 */ 
export const useLogoutQueryKey = () => ['logout'] as QueryKey;

/**
 * Logout mutation hook for tanstack query
 */
export const useLogoutMutation = (
  options?: UseMutationOptions<
    { status: boolean; data?: void; error?: { code: string; message: string } },
    Error,
    void
  >
) =>
  useMutation({
    mutationFn: authService.logout,
    mutationKey: useLogoutQueryKey(),
    ...options,
    onSuccess: (data) => {
      // Ensure all auth state is cleared after logout succeeds
      useUser.getState().logoutUser();
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      // Still clear state on logout error
      useUser.getState().logoutUser();
      options?.onError?.(error);
    },
  });
